'use client'

import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import Link from 'next/link'
import { Copy, Check, ExternalLink, Clock, ShieldCheck, Eye, X, Truck, ArrowRight, ChevronDown, Tag, Zap } from 'lucide-react'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, calculateOfferDuration } from '@/constants/hooks'
import { Offer } from '@/services/dataTypes'
import Theme18OfferLink from './Theme18OfferLink'
import { copyToClipboard } from '../utils/clipboard'
import DiscountTag from './DiscountTag'

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    ORANGE: '#FF9934',
    LIGHT: '#E1F1F2',
    BORDER: '#f0ede8',
    BORDER_ACCENT: '#ffe0c2',
}

interface Props {
    product: Offer
    domain: string
    merchantHref: string
    merchant_name: string
    merchant_logo: string
    companyId?: string
    productDetailUrl?: string | null
    onOfferClick?: () => void
}

// ─── Modal ─── //

function OfferModal({
    product, domain, merchantHref, merchant_name, merchant_logo, onClose, currency,
}: {
    product: Offer; domain: string; merchantHref: string
    merchant_name: string; merchant_logo: string
    onClose: () => void; currency: string
}) {
    const [copied, setCopied] = useState(false)
    const [revealed, setRevealed] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const imageUrl = getBaseImageUrl(domain, merchant_logo, '')
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const isCoupon = !product?.coupon_code
    const imgSrc = (product?.offer_type?.name === 'product' && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '') : imageUrl

    const handleCopy = async () => {
        if (!revealed) { setRevealed(true); return }
        await copyToClipboard(product.coupon_code?.trim() ?? '')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col"
                style={{ background: '#ffffff', border: `1.5px solid ${C.BORDER_ACCENT}`, boxShadow: '0 24px 64px rgba(140,88,38,0.18)', maxHeight: '90vh' }}>

                {/* Header band */}
                <div className="px-6 py-5 flex items-start gap-4"
                    style={{
                        background: isCoupon
                            ? `linear-gradient(135deg, ${C.PRIMARY}35 0%, #fffaf5 100%)`
                            : `linear-gradient(135deg, ${C.LIGHT} 0%, #fffaf5 100%)`,
                        borderBottom: `1px solid ${C.BORDER_ACCENT}`,
                    }}>
                    <div className="w-18 h-18 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0 p-2.5"
                        style={{ background: '#ffffff', border: `1.5px solid ${C.BORDER_ACCENT}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Image src={imgSrc} alt={merchant_name} width={68} height={68} className="object-contain w-full h-full" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <Link href={merchantHref} onClick={onClose}
                            className="text-xs font-bold uppercase tracking-wider truncate block hover:opacity-70 transition-opacity"
                            style={{ color: C.BROWN }}>
                            {merchant_name}
                        </Link>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {isCoupon ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black"
                                    style={{ background: C.ORANGE, color: '#ffffff' }}>
                                    <Tag size={9} /> Coupon Code
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black"
                                    style={{ background: C.SECONDARY, color: '#ffffff' }}>
                                    <Zap size={9} /> Deal
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all hover:scale-110 cursor-pointer"
                        style={{ background: 'rgba(140,88,38,0.1)', color: C.BROWN }}>
                        <X size={15} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                    {/* Title */}
                    <h2 className="text-base font-black leading-snug" style={{ color: '#1a1a1a' }}>
                        {discardHTMLTags(product?.offer_title)}
                    </h2>

                    {/* Description with See More */}
                    {product?.offer_detail && (() => {
                        const cleanDetail = discardHTMLTags(product.offer_detail)
                        const isLong = cleanDetail.length > 90
                        return (
                            <div className="space-y-1">
                                <div className="p-3.5 rounded-2xl bg-gray-50/80 border border-gray-100">
                                    <p className={`text-xs sm:text-sm leading-relaxed text-gray-600 ${isLong && !isExpanded ? 'line-clamp-2 sm:line-clamp-3' : ''}`}>
                                        {cleanDetail}
                                    </p>
                                    {isLong && (
                                        <button
                                            type="button"
                                            onClick={() => setIsExpanded(!isExpanded)}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold transition-colors cursor-pointer"
                                            style={{ color: C.BROWN }}
                                        >
                                            <span>{isExpanded ? 'See Less' : 'See More'}</span>
                                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })()}

                    {/* Price */}
                    {salePrice > 0 && (
                        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl flex-wrap"
                            style={{ background: `${C.LIGHT}60`, border: `1px solid ${C.BORDER_ACCENT}` }}>
                            <span className="text-2xl font-black" style={{ color: C.BROWN }}>
                                {currency}{salePrice.toFixed(2)}
                            </span>
                            {originalPrice > 0 && (
                                <span className="text-sm line-through" style={{ color: '#d1d5db' }}>
                                    {currency}{originalPrice.toFixed(2)}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Coupon code — reveal + copy */}
                    {isCoupon && product.coupon_code && (
                        <div className="space-y-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: C.BROWN }}>
                                Coupon Code
                            </p>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-2xl"
                                    style={{ background: `${C.PRIMARY}20`, border: `1.5px dashed ${C.ORANGE}` }}>
                                    <Tag size={14} style={{ color: C.ORANGE, flexShrink: 0 }} />
                                    <code className="text-sm font-mono font-black flex-1 truncate"
                                        style={{
                                            color: C.BROWN,
                                            filter: revealed ? 'none' : 'blur(6px)',
                                            userSelect: revealed ? 'auto' : 'none',
                                            transition: 'filter 0.25s',
                                        }}>
                                        {product.coupon_code}
                                    </code>
                                </div>
                                <button onClick={handleCopy}
                                    className="flex items-center gap-1.5 px-4 py-3 rounded-2xl text-xs font-black transition-all hover:scale-105 active:scale-95 flex-shrink-0 cursor-pointer"
                                    style={{ background: C.BROWN, color: '#ffffff' }}>
                                    {!revealed ? 'Reveal'
                                        : copied ? <><Check size={12} /> Copied!</>
                                            : <><Copy size={12} /> Copy</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Verified badge */}
                    <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: '#22c55e' }}>
                        <ShieldCheck size={13} /> Verified &amp; Active Deal
                    </div>
                </div>

                {/* Footer CTA (Theme 5 OutUrl Logic) */}
                <div className="px-6 py-4" style={{ borderTop: `1px solid ${C.BORDER_ACCENT}` }}>
                    <div className="rounded-2xl overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:brightness-110 active:scale-[0.98]"
                        style={{ background: `linear-gradient(135deg, ${C.SECONDARY} 0%, #1a9fa7 100%)` }}>
                        <Theme18OfferLink
                            unique_id={product?.unique_id}
                            outUrl={product?.url || merchantHref}
                            merchantHref={merchantHref}
                            domain={domain}
                            couponCode={product?.coupon_code || undefined}
                            customClass="flex items-center justify-center gap-2 px-6 py-3 text-sm font-black text-white cursor-pointer">
                            <span>{isCoupon ? 'Shop Now' : 'Get Deal'}</span>
                            <ExternalLink size={13} />
                        </Theme18OfferLink>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

// ─── Card ─── //

const StoreOfferCard = ({
    product, merchantHref, domain, merchant_name, merchant_logo, companyId = '', productDetailUrl, onOfferClick,
}: Props) => {
    const [showModal, setShowModal] = useState(false)
    const [copied, setCopied] = useState(false)

    const imageUrl = getBaseImageUrl(domain, merchant_logo, '')
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPct = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalTag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPct)
    const isCoupon = !product?.coupon_code
    const currency = getCurrencySymbol(product?.currency)
    const duration = product?.end_date ? calculateOfferDuration(product.end_date) : null
    const isExpired = duration === 'Offer expired'
    const imgSrc = (product?.offer_type?.name === 'product' && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '') : imageUrl
    const isFreeShipping = product?.keywords?.toLowerCase().includes('free shipping') || product?.offer_detail?.toLowerCase().includes('free shipping') || product?.offer_title?.toLowerCase().includes('free shipping')

    const handleCopyInline = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (product?.coupon_code) {
            await copyToClipboard(product.coupon_code.trim())
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const handleDetailsTrigger = () => {
        if (onOfferClick) {
            onOfferClick()
        } else {
            setShowModal(true)
        }
    }

    return (
        <>
            <article
                className="group rounded-2xl md:rounded-3xl overflow-hidden flex flex-col md:flex-row transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 md:hover:-translate-y-1.5 border border-[#f0ede8] hover:border-[#F8F246] bg-white relative p-3 sm:p-4 md:p-0 gap-3 md:gap-0"
                style={{
                    boxShadow: '0 4px 20px rgba(140,88,38,0.05)',
                    opacity: isExpired ? 0.65 : 1,
                }}
            >
                {/* ── Mobile Top Row / Desktop Left & Middle Sections ── */}
                <div className="flex flex-row md:contents gap-3 items-start md:items-stretch">
                    {/* ── Left Hero Image Section (Theme 5 OutUrl Clickable) ── */}
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-56 lg:w-60 md:min-h-[200px] bg-gradient-to-br from-[#fffdfa] via-[#fcfbf9] to-[#f7f5f0] border border-[#f0ede8] md:border-0 md:border-b-0 md:border-r rounded-xl md:rounded-none flex items-center justify-center p-2 sm:p-3 md:p-6 overflow-hidden flex-shrink-0">
                        {/* Ambient Glow */}
                        <div className="absolute inset-0 bg-radial-gradient from-[#F8F246]/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                        {/* Product / Merchant Image Link */}
                        <Theme18OfferLink
                            unique_id={product?.unique_id}
                            outUrl={product?.url || merchantHref}
                            merchantHref={merchantHref}
                            domain={domain}
                            couponCode={product?.coupon_code || undefined}
                            customClass="relative w-full h-full flex items-center justify-center z-10 block"
                        >
                            <Image
                                src={imgSrc}
                                alt={discardHTMLTags(product?.offer_title) || merchant_name}
                                width={160}
                                height={160}
                                className="object-contain max-h-16 sm:max-h-20 md:max-h-36 w-auto transition-transform duration-500 group-hover:scale-110 drop-shadow-xs"
                                loading="lazy"
                            />
                        </Theme18OfferLink>

                        {/* Top Left: Discount Badge (Desktop) */}
                        {finalTag && (
                            <div className="hidden md:block absolute top-3 left-3 z-20">
                                <DiscountTag tag={finalTag} />
                            </div>
                        )}

                        {/* Top Right: Type Badge (Desktop) */}
                        <div className="hidden md:block absolute top-3 right-3 z-20">
                            {isCoupon ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-xl shadow-sm tracking-wider"
                                    style={{ background: C.PRIMARY, color: C.BROWN }}>
                                    <Tag size={10} /> CODE
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-xl shadow-sm tracking-wider"
                                    style={{ background: '#e0f7f9', color: C.SECONDARY }}>
                                    <Zap size={10} /> DEAL
                                </span>
                            )}
                        </div>

                        {/* Bottom Left: Free shipping badge (Desktop) */}
                        {isFreeShipping && (
                            <div className="hidden md:block absolute bottom-3 left-3 z-20">
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-lg shadow-sm"
                                    style={{ background: C.SECONDARY, color: '#ffffff' }}>
                                    <Truck size={9} /> Free Shipping
                                </span>
                            </div>
                        )}
                    </div>

                    {/* ── Middle: Main Offer Details ── */}
                    <div className="flex-1 md:p-5 lg:p-6 flex flex-col justify-between gap-2 md:gap-3 min-w-0">
                        <div className="space-y-1.5 md:space-y-2">
                            {/* Badges Row */}
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                {/* Mobile Badges */}
                                {finalTag && (
                                    <span className="md:hidden inline-flex items-center gap-0.5 text-[9px] font-black px-1.5 py-0.5 rounded-md text-white"
                                        style={{ background: C.ORANGE }}>
                                        {finalTag}
                                    </span>
                                )}
                                <span className="inline-flex md:hidden items-center gap-0.5 text-[9px] font-black px-1.5 py-0.5 rounded-md"
                                    style={isCoupon ? { background: C.PRIMARY, color: C.BROWN } : { background: '#e0f7f9', color: C.SECONDARY }}>
                                    {isCoupon ? 'CODE' : 'DEAL'}
                                </span>
                                <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-emerald-600">
                                    <ShieldCheck size={11} className="text-emerald-500" /> Verified
                                </span>
                                {product?.category?.name && (
                                    <>
                                        <span className="hidden sm:inline text-gray-300">&bull;</span>
                                        <span className="hidden sm:inline text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 truncate max-w-[120px]">
                                            {product.category.name}
                                        </span>
                                    </>
                                )}
                            </div>

                            {/* Offer Title */}
                            {productDetailUrl && product?.is_detail === 1 ? (
                                <Link href={productDetailUrl}
                                    className="block text-xs sm:text-sm md:text-base lg:text-lg font-black leading-snug hover:text-amber-800 transition-colors line-clamp-2"
                                    style={{ color: '#1a1a1a' }}>
                                    {discardHTMLTags(product?.offer_title)}
                                </Link>
                            ) : (
                                <Theme18OfferLink
                                    unique_id={product?.unique_id}
                                    outUrl={product?.url || merchantHref}
                                    merchantHref={merchantHref}
                                    domain={domain}
                                    couponCode={product?.coupon_code || undefined}
                                    customClass="block"
                                >
                                    <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-black leading-snug line-clamp-2 hover:text-amber-800 transition-colors" style={{ color: '#1a1a1a' }}>
                                        {discardHTMLTags(product?.offer_title)}
                                    </h3>
                                </Theme18OfferLink>
                            )}

                            {/* Description (Hidden on mobile for compactness, visible on tablet/desktop) */}
                            {product?.offer_detail && (
                                <p className="hidden sm:block text-xs md:text-sm leading-relaxed line-clamp-1 md:line-clamp-2 text-gray-500">
                                    {discardHTMLTags(product?.offer_detail)}
                                </p>
                            )}
                        </div>

                        {/* Pricing Block & Bottom Info */}
                        <div className="flex items-center justify-between gap-2 flex-wrap pt-1.5 md:pt-2 border-t border-[#f0ede8] mt-auto">
                            {salePrice > 0 ? (
                                <div className="flex items-baseline gap-1.5 sm:gap-2">
                                    <span className="text-base sm:text-lg md:text-2xl font-black" style={{ color: '#1a1a1a' }}>
                                        {currency}{salePrice.toFixed(2)}
                                    </span>
                                    {originalPrice > 0 && (
                                        <span className="text-[11px] sm:text-xs md:text-sm line-through font-medium text-gray-400">
                                            {currency}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {discountPct && discountPct > 0 && (
                                        <span className="hidden sm:inline-block text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100">
                                            Save {discountPct}%
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div />
                            )}

                            <div className="flex items-center gap-2 sm:gap-3">
                                <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium"
                                    style={{ color: isExpired ? '#ef4444' : '#6b7280' }}>
                                    <Clock size={11} className={isExpired ? 'text-rose-500' : 'text-gray-400'} />
                                    <span className={isExpired ? 'font-bold text-rose-600' : ''}>{duration ?? ''}</span>
                                </span>

                                <button
                                    onClick={handleDetailsTrigger}
                                    className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors"
                                >
                                    <Eye size={11} />
                                    <span>Details</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: Action / CTA Box (Theme 5 Click & Tracking Logic) ── */}
                <div className="w-full md:w-56 p-0 md:py-6 md:pl-4 md:pr-6 md:border-l md:border-[#f0ede8] flex flex-col justify-center gap-2 sm:gap-2.5 md:gap-3 bg-transparent md:bg-none flex-shrink-0 border-t md:border-t-0 pt-2.5 md:pt-0 border-gray-100">



                    {/* Primary CTA Button — Exact Theme 5 Dual Action (Open Store Tab + Redirect Tracking Link + Copy Code) */}
                    <Theme18OfferLink
                        unique_id={product?.unique_id}
                        outUrl={product?.url || merchantHref}
                        merchantHref={merchantHref}
                        domain={domain}
                        couponCode={product?.coupon_code || undefined}
                        customClass="w-full block"
                    >
                        <div
                            className="w-full flex items-center justify-center gap-1.5 py-2.5 sm:py-3 md:py-3.5 px-4 text-xs font-bold tracking-wider rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-xs md:shadow-md hover:shadow-lg cursor-pointer"
                            style={isCoupon
                                ? { background: C.BROWN, color: '#ffffff', boxShadow: `0 3px 10px rgba(140, 88, 38, 0.2)` }
                                : { background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffd452 100%)`, color: C.BROWN, border: '1px solid rgba(140, 88, 38, 0.15)', boxShadow: `0 3px 10px rgba(248, 242, 70, 0.25)` }}>
                            <span>{isCoupon ? 'Get Code & Shop' : (product?.offer_type?.name === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                            <ArrowRight size={12} />
                        </div>
                    </Theme18OfferLink>

                    <div className="hidden sm:flex items-center justify-center gap-1 text-[9px] sm:text-[10px] text-gray-400 font-semibold">
                        <ShieldCheck size={11} className="text-emerald-500" />
                        <span>Guaranteed Working</span>
                    </div>
                </div>
            </article>

            {/* ── Details Modal ── */}
            {showModal && (
                <OfferModal
                    product={product}
                    domain={domain}
                    merchantHref={merchantHref}
                    merchant_name={merchant_name}
                    merchant_logo={merchant_logo}
                    currency={currency}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    )
}

export default StoreOfferCard
